import React, { useState } from "react"

import { ActionDialog } from "@/components"
import { renderComponent } from "@/utils/node"

import useDidMount from "@/hooks/useDidMount"
import useValidation, { ErrorType } from "@/hooks/useValidation"

import MeetingAnalyzerService from "@/services/MeetingAnalyzerService"
import UserInInstanceService from "@/services/UserInInstance"
import { UserInInstance } from "@/protocols/userInInstance"

import { useGlobalStateStore } from "@/store/GlobalState"

import FormContent from "@/pages/Admin/MeetingAnalyzer/CreateDialog/FormContent"
import LoadingContent from "@/pages/Admin/MeetingAnalyzer/CreateDialog/LoadingContent"

const MAX_TRANSCRIPTION_LENGTH = 80000

type CreateDialogProps = {
	onSuccess?: (analysisId: number) => void
	isDefaultOpen?: boolean
}

type CreateDialogType = {
	open: (props: CreateDialogProps) => void
}

const CreateDialog: CreateDialogType & React.FC<CreateDialogProps> = (props) => {
	const {
		onSuccess,
		isDefaultOpen
	} = props

	const useValidationOutput = useValidation()

	const [isOpen, setIsOpen] = useState<boolean>(isDefaultOpen || false)
	const [transcription, setTranscription] = useState("")
	const [selectedCloserId, setSelectedCloserId] = useState<number | null>(null)
	const [closers, setClosers] = useState<UserInInstance[]>([])
	const [isAnalyzing, setIsAnalyzing] = useState(false)

	const globalStateStore = useGlobalStateStore()
	const currentUserInInstanceId = globalStateStore?.instance?.user_in_instance_id

	const loadClosers = async () => {
		const closersList = await UserInInstanceService.getUsersInInstance({ status: ["active"] })

		if (closersList) {
			setClosers(closersList)
		}

		setSelectedCloserId(currentUserInInstanceId ?? null)
	}

	useDidMount(() => {
		loadClosers()
	})

	const handleClose = () => {
		if (isAnalyzing) {
			return
		}

		setIsOpen(false)
		useValidationOutput.clearAllValidations()
	}

	const handleTranscriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value.slice(0, MAX_TRANSCRIPTION_LENGTH)

		setTranscription(value)
		useValidationOutput.clearValidation("transcription")
	}

	const handleCloserChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setSelectedCloserId(Number(event.target.value))
		useValidationOutput.clearValidation("userId")
	}

	/**
	 * WARNING:
	 * - Validation is handled client-side to avoid UI flickering. Because this screen has a full-dialog
	 * loading state (rather than a button spinner), triggering a server round-trip for validation would
	 * cause a visible flicker even when required fields are empty. Client-side validation prevents that.
	 */
	const validateFields = () => {
		const EMPTY_FIELD_TEXT = "O campo não pode estar vazio"

		const validationObject: Record<string, string | null> = {}

		validationObject.transcription = !transcription ? EMPTY_FIELD_TEXT : null
		validationObject.userId = !selectedCloserId ? EMPTY_FIELD_TEXT : null

		useValidationOutput.addValidation(validationObject)

		const isThereAnyInvalidField = Object.values(validationObject).some((errorCode) => Boolean(errorCode))

		return isThereAnyInvalidField
	}

	const handleCreateAnalysis = async () => {
		const isThereAnyInvalidField = validateFields()

		if (isThereAnyInvalidField) {
			return
		}

		useValidationOutput.clearValidation("transcription")
		setIsAnalyzing(true)

		try {
			const analysis = await MeetingAnalyzerService.create({
				userId: selectedCloserId as number,
				transcription
			})

			onSuccess?.(analysis.id)
			handleClose()
		} catch (error) {
			useValidationOutput.triggerValidation(error as ErrorType)
		}

		setIsAnalyzing(false)
	}

	return (
		<ActionDialog
			title="Analisador de reunião"
			openDialog={isOpen}
			onClose={handleClose}
			loading={isAnalyzing}
			hideCloseIcon={isAnalyzing}
			hideCloseButton={true}
			fullWidth
			maxWidth="md"
		>
			{isAnalyzing ? (
				<LoadingContent />
			) : (
				<FormContent
					transcription={transcription}
					selectedCloserId={selectedCloserId}
					closers={closers}
					useValidationOutput={useValidationOutput}
					isAnalyzing={isAnalyzing}
					currentUserInInstanceId={currentUserInInstanceId}
					onTranscriptionChange={handleTranscriptionChange}
					onCloserChange={handleCloserChange}
					onSubmit={handleCreateAnalysis}
				/>
			)}
		</ActionDialog>
	)
}

CreateDialog.open = (props: CreateDialogProps) => {
	renderComponent(
		"meeting-analyzer-create-dialog",
		<CreateDialog
			{...props}
			isDefaultOpen={true}
		/>
	)
}

export default CreateDialog
